import React from 'react';
import {Button, Input, Select, Space} from 'antd';
import {useTranslation} from 'react-i18next';
import {PenaltyCenterMode, PenaltySource} from '../graphql/types';
import {PenaltyCenterFilters} from '../utils/penaltyCenterUtils';

interface PenaltyCenterFilterProps {
    filters: PenaltyCenterFilters;
    onChange: (filters: PenaltyCenterFilters) => void;
    onSearch: () => void;
    onReset: () => void;
    /** 按钮区左侧的额外操作（如导出） */
    extraActions?: React.ReactNode;
    /** warehouse 模式多一列 DSP 下拉（DSP 侧只能看自己的罚单，没有这个维度） */
    mode?: PenaltyCenterMode;
    /** 仅 warehouse 模式传，来自 getAllDspsForWmsPermission（该仓关联的 DSP） */
    dspOptions?: {value: number; label: string}[];
    dspLoading?: boolean;
}

/**
 * label 置于输入框上方的筛选项。
 * 不复用 ParcelFilterStyle / BatchInputFilterItem —— 它们经 useFilterLabelClassName
 * 固定为「label 在左」的行内布局，无法改成上下结构。
 */
const FilterField: React.FC<{label: string; children: React.ReactNode}> = ({label, children}) => (
    <div className="min-w-0">
        <div className="mb-1 text-sm text-gray-600 truncate" title={label}>{label}</div>
        {children}
    </div>
);

const PenaltyCenterFilter: React.FC<PenaltyCenterFilterProps> = ({
    filters,
    onChange,
    onSearch,
    onReset,
    extraActions,
    mode = 'dsp',
    dspOptions = [],
    dspLoading = false,
}) => {
    const {t} = useTranslation();
    const isWarehouse = mode === 'warehouse';

    // 来源是后端固定 5 枚举，写死即可，不需要选项接口
    const sourceOptions = Object.values(PenaltySource).map((code) => ({
        value: code,
        label: t(`partnerControl.penaltyCenter.penaltySource.${code}`, {defaultValue: code}),
    }));

    return (
        <div>
            {/* 一行：DSP 侧 [运单号][罚单来源][派送司机]；warehouse 侧前面多一列 [DSP] */}
            <div
                className={`grid ${isWarehouse ? 'grid-cols-4' : 'grid-cols-3'} gap-x-6 items-start`}
            >
                {isWarehouse && (
                    <FilterField label={String(t('partnerControl.penaltyCenter.filter.dsp'))}>
                        <Select
                            className="w-full"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            loading={dspLoading}
                            options={dspOptions}
                            value={filters.dspId}
                            placeholder={t('partnerControl.penaltyCenter.filter.dsp') as string}
                            onChange={(value?: number) => onChange({...filters, dspId: value})}
                        />
                    </FilterField>
                )}
                <FilterField label={String(t('partnerControl.penaltyCenter.filter.trackingNumber'))}>
                    <Input
                        className="w-full"
                        value={filters.trackingNumbers}
                        placeholder={
                            t('partnerControl.penaltyCenter.filter.trackingNumberPlaceholder') as string
                        }
                        onChange={(e) =>
                            onChange({...filters, trackingNumbers: e.target.value || undefined})
                        }
                        allowClear
                    />
                </FilterField>
                <FilterField label={String(t('partnerControl.penaltyCenter.filter.penaltySource'))}>
                    <Select
                        className="w-full"
                        mode="multiple"
                        maxTagCount="responsive"
                        allowClear
                        options={sourceOptions}
                        value={filters.penaltySources ?? []}
                        placeholder={t('partnerControl.penaltyCenter.filter.penaltySource') as string}
                        onChange={(value: PenaltySource[]) =>
                            onChange({...filters, penaltySources: value.length ? value : undefined})
                        }
                    />
                </FilterField>
                <FilterField label={String(t('partnerControl.penaltyCenter.filter.driverName'))}>
                    <Input
                        className="w-full"
                        value={filters.driverName}
                        placeholder={
                            t('partnerControl.penaltyCenter.filter.driverNamePlaceholder') as string
                        }
                        onChange={(e) => onChange({...filters, driverName: e.target.value || undefined})}
                        allowClear
                    />
                </FilterField>
            </div>
            <div className="flex justify-end items-center mt-4">
                <Space>
                    {extraActions}
                    <Button onClick={onReset} className="w-auto min-w-24">
                        <span className="font-bold">{t('partnerControl.penaltyCenter.filter.reset')}</span>
                    </Button>
                    <Button type="primary" onClick={onSearch} className="w-auto min-w-24">
                        <span className="font-bold">{t('partnerControl.penaltyCenter.filter.search')}</span>
                    </Button>
                </Space>
            </div>
        </div>
    );
};

export default PenaltyCenterFilter;
